// src/components/organisms/PatientWorkspaceTabs/NotificationEditor.js
//
// Bildirim ayarı editörü — düz editör dili (bkz. _editorParts). Kayıt elle
// "Kaydet" ile yapılır; son bölüm bu ayarın gönderim geçmişi.

import React, { useState, useCallback } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import {
  Delete as DeleteIcon,
  RadioButtonUnchecked as OptionIcon,
  ListAlt as OptionsIcon,
  NotificationsNone as NotificationsNoneIcon,
} from "@mui/icons-material";

import FormTextField from "../../atoms/FormTextField";
import ChoiceChipGroup from "../../atoms/ChoiceChipGroup";
import ActionButton from "../../atoms/ActionButton";
import { useClinicTheme } from "../../../theme";
import { COLORS } from "../../../styles/uiConstants";
import useInlineAutosave from "../../../hooks/useInlineAutosave";
import {
  EDITOR_PX,
  SECTION_SX,
  ITEM_ROW_SX,
  ITEM_NAME_SX,
  listAreaSx,
  FieldRow,
  SectionHeader,
  SectionEmpty,
  EditorHeader,
} from "./_editorParts";
import TextItemDrawer from "./drawers/TextItemDrawer";
import { NotificationLogRow } from "./drawers/NotificationLogDrawer";

import {
  useCreateNotificationSettingMutation,
  useUpdateNotificationSettingMutation,
} from "../../../store/secretaryApi";
import useTerms from "../../../hooks/useTerms";

const FREQUENCY_OPTIONS = [
  { value: "once", label: "Bir kez" },
  { value: "daily", label: "Günlük" },
  { value: "weekly", label: "Haftalık" },
  { value: "monthly", label: "Aylık" },
];

const RESPONSE_OPTIONS = [
  { value: "none", label: "Cevap yok" },
  { value: "yes_no", label: "Evet / Hayır" },
  { value: "multiple_choice", label: "Çoktan seçmeli" },
  { value: "free_text", label: "Düz yazı" },
];

const OPTION_LIST_HEIGHT = 168;
const LOG_LIST_MIN = 200;

const settingFieldsFromRecord = (record) => ({
  subject: record?.subject || "",
  message: record?.message || "",
  sendTime: record?.sendTime || "09:00",
  sendFrequency: record?.sendFrequency || "once",
  responseFormat: record?.responseFormat || "none",
  responseOptions: Array.isArray(record?.responseOptions)
    ? [...record.responseOptions]
    : [],
});

/**
 * @param {object|null} record
 * @param {object[]} [logs] - Bu ayara ait gönderimler (yeniden eskiye)
 */
export default function NotificationEditor({
  record,
  logs = [],
  patientId,
  token,
  canEdit,
  onCreated,
  onSaved,
  onDelete,
  onSendNow,
  onError,
}) {
  const terms = useTerms();
  const { palette } = useClinicTheme();
  const [optionDrawer, setOptionDrawer] = useState(false);

  const [createSetting] = useCreateNotificationSettingMutation();
  const [updateSetting] = useUpdateNotificationSettingMutation();

  const createFn = useCallback(
    async (payload) => {
      const res = await createSetting(payload).unwrap();
      return res?.data?.notificationSetting || res?.notificationSetting || res?.data?.setting || res;
    },
    [createSetting]
  );
  const updateFn = useCallback(
    async (payload) => {
      const res = await updateSetting(payload).unwrap();
      return res?.data?.notificationSetting || res?.notificationSetting || res?.data?.setting || res;
    },
    [updateSetting]
  );

  // Bildirim ayarları her zaman manualCreate (Hasta detay & muayene fark etmez).
  const { fieldValues, setField, saveStatus, saveNow } = useInlineAutosave({
    record,
    createMutation: createFn,
    updateMutation: updateFn,
    fieldsFromRecord: settingFieldsFromRecord,
    shouldCreate: (form) => Boolean(form.subject?.trim()),
    debounceMs: 500,
    manualCreate: true,
    buildPayload: (form) => ({
      token,
      patientId,
      subject: form.subject,
      message: form.message,
      sendTime: form.sendTime,
      sendFrequency: form.sendFrequency,
      responseFormat: form.responseFormat,
      responseOptions:
        form.responseFormat === "multiple_choice" ? form.responseOptions : [],
    }),
    recordIdField: "notificationSettingId",
    onCreated,
    onSaved,
    onError,
  });

  const isDisabled = !canEdit;
  const isNew = !record?._id;
  const options = fieldValues.responseOptions || [];
  const multiple = fieldValues.responseFormat === "multiple_choice";

  const removeOption = (idx) => setField("responseOptions", options.filter((_, i) => i !== idx));

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      <EditorHeader
        record={record}
        newLabel="Yeni bildirim oluşturuluyor"
        editLabel="Bildirim düzenleniyor"
        palette={palette}
        actions={
          !isNew && !isDisabled ? (
            <Box sx={{ display: "flex", gap: 1 }}>
              {typeof onSendNow === "function" && (
                <ActionButton
                  variant="send"
                  tone="soft"
                  compact
                  palette={palette}
                  label="Hemen Gönder"
                  onClick={() => onSendNow(record)}
                />
              )}
              {typeof onDelete === "function" && (
                <ActionButton variant="delete" tone="soft" compact label="Sil" onClick={() => onDelete(record)} />
              )}
            </Box>
          ) : null
        }
      />

      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", display: "flex", flexDirection: "column" }}>
        {/* İçerik */}
        <Box sx={SECTION_SX}>
          <FieldRow label="Konu *">
            <FormTextField
              palette={palette}
              value={fieldValues.subject || ""}
              onChange={(e) => setField("subject", e.target.value)}
              disabled={isDisabled}
              variant="standard"
              size="small"
              fullWidth
              placeholder="Ör. Tansiyon ölçüm hatırlatması"
              inputProps={{ "aria-label": "Konu" }}
            />
          </FieldRow>
          <FieldRow label="Mesaj">
            <FormTextField
              palette={palette}
              value={fieldValues.message || ""}
              onChange={(e) => setField("message", e.target.value)}
              disabled={isDisabled}
              variant="standard"
              size="small"
              fullWidth
              multiline
              minRows={2}
              placeholder={`${terms.patientGen} telefonunda görünecek metin`}
              inputProps={{ "aria-label": "Mesaj" }}
            />
          </FieldRow>
        </Box>

        {/* Zamanlama + cevap */}
        <Box sx={SECTION_SX}>
          <FieldRow label="Saat">
            <FormTextField
              palette={palette}
              type="time"
              value={fieldValues.sendTime || "09:00"}
              onChange={(e) => setField("sendTime", e.target.value)}
              disabled={isDisabled}
              variant="standard"
              size="small"
              sx={{ maxWidth: 120 }}
              inputProps={{ "aria-label": "Gönderim saati" }}
            />
          </FieldRow>
          <FieldRow label="Sıklık">
            <ChoiceChipGroup
              options={FREQUENCY_OPTIONS}
              value={fieldValues.sendFrequency || "once"}
              onChange={(v) => setField("sendFrequency", v)}
              disabled={isDisabled}
              ariaLabel="Sıklık"
            />
          </FieldRow>
          <FieldRow label="Cevap tipi">
            <ChoiceChipGroup
              options={RESPONSE_OPTIONS}
              value={fieldValues.responseFormat || "none"}
              onChange={(v) => setField("responseFormat", v)}
              disabled={isDisabled}
              ariaLabel="Cevap tipi"
            />
          </FieldRow>
        </Box>

        {/* Şıklar (yalnız çoktan seçmeli) */}
        {multiple ? (
          <Box sx={SECTION_SX}>
            <SectionHeader
              title="Şıklar"
              count={options.length}
              addLabel="Şık Ekle"
              onAdd={isDisabled ? undefined : () => setOptionDrawer(true)}
              palette={palette}
            />
            <Box sx={listAreaSx(OPTION_LIST_HEIGHT, options.length === 0)}>
              {options.length === 0 ? (
                <SectionEmpty
                  icon={<OptionsIcon />}
                  title="Henüz şık eklenmedi"
                  hint={isDisabled ? undefined : `${terms.patientGen} seçeceği cevapları Şık Ekle ile girin.`}
                />
              ) : (
                <Box>
                  {options.map((opt, idx) => (
                    <Box key={`${opt}-${idx}`} sx={ITEM_ROW_SX}>
                      <OptionIcon sx={{ fontSize: 16, color: palette.primary, flexShrink: 0 }} />
                      <Typography noWrap sx={{ ...ITEM_NAME_SX, flex: 1 }}>{opt}</Typography>
                      {isDisabled ? null : (
                        <IconButton
                          size="small"
                          aria-label={`${opt} kaldır`}
                          onClick={() => removeOption(idx)}
                          sx={{ color: COLORS.slate400, "&:hover": { color: COLORS.errorDark } }}
                        >
                          <DeleteIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        ) : null}

        {/* Bu ayarın gönderim geçmişi — son bölüm, kalan alanı doldurur */}
        {!isNew ? (
          <Box sx={{ ...SECTION_SX, borderBottom: "none", flex: 1, minHeight: LOG_LIST_MIN }}>
            <SectionHeader title="Gönderim Geçmişi" count={logs.length} />
            <Box sx={{ ...listAreaSx("auto", logs.length === 0), flex: 1 }}>
              {logs.length === 0 ? (
                <SectionEmpty
                  icon={<NotificationsNoneIcon />}
                  title="Henüz gönderilmedi"
                  hint="Saati geldiğinde ya da Hemen Gönder ile gönderildiğinde burada görünür."
                />
              ) : (
                logs.map((log) => <NotificationLogRow key={log._id} log={log} showSubject={false} />)
              )}
            </Box>
          </Box>
        ) : null}
      </Box>

      {!isDisabled && (
        <Box
          sx={{
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 1.5,
            ...EDITOR_PX,
            py: 1.5,
            borderTop: `1px solid ${COLORS.slate100}`,
          }}
        >
          <ActionButton
            variant="save"
            palette={palette}
            onClick={() => saveNow()}
            disabled={!fieldValues.subject?.trim() || saveStatus === "saving"}
            loading={saveStatus === "saving"}
            label={isNew ? "Bildirimi Kaydet" : "Kaydet"}
          />
        </Box>
      )}

      <TextItemDrawer
        open={optionDrawer}
        onClose={() => setOptionDrawer(false)}
        icon={<OptionsIcon />}
        title="Şık Ekle"
        subtitle={`${terms.patientGen} bildirimde seçebileceği bir cevap`}
        label="Şık"
        placeholder="Ör. Bugün ölçtüm"
        existing={options}
        onSubmit={(text) => {
          setField("responseOptions", [...options, text]);
          setOptionDrawer(false);
        }}
      />
    </Box>
  );
}
